import { useState, useRef } from "react"
import { useParams, useNavigate } from "react-router"
import {
    Box, Button, ButtonGroup, Divider, Flex, Input, ModalBody, ModalFooter,
    Progress, Spinner, Td, Text, Textarea, Badge, useDisclosure, useToast,
    useColorModeValue, Tooltip
} from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Modal, Row, Table, Tag } from "components"
import { API, Enums } from "config"
import { useInvoice } from "hooks"
import { Authenticate } from "screens"

export default function InvoiceDetail() {
    const { id }   = useParams()
    const navigate = useNavigate()
    const toast    = useToast()
    const invoice  = useInvoice(id)
    const receive  = useDisclosure()
    const reject   = useDisclosure()

    const data = invoice.data
    const cancel = useDisclosure()

    if (invoice.isLoading) return <Container maxW="90%" mt={10}><Spinner /></Container>

    const status    = Enums.InvoiceStatus.data(data?.status)
    const canAct    = [Enums.InvoiceStatus.ARRIVING, Enums.InvoiceStatus.PARTIALLY_RECEIVED].includes(data?.status)
    const canCancel = data?.status === Enums.InvoiceStatus.FULLY_RECEIVED

    return (
        <Container maxW="90%" mt={10}>
            <Heading title={
            data?.number ? (
                <Tooltip label="Clique para copiar o número" hasArrow placement="top">
                    <Box as="span" cursor="pointer"
                        _hover={{ color: 'teal.500' }} transition="color 0.15s"
                        onClick={() => {
                            navigator.clipboard.writeText(data.number)
                            toast({ title: `NF-e ${data.number} copiada`, status: 'info', duration: 1800, isClosable: true })
                        }}
                    >
                        NF-e #{data.number}
                    </Box>
                </Tooltip>
            ) : 'NF-e ...'
        }>
                <ButtonGroup>
                    <IconButton size="xs" icon="left" label="Voltar" onClick={() => navigate('/stock/invoices')} />
                    <IconButton size="xs" icon="refresh" isLoading={invoice.isFetching} onClick={invoice.refetch} />
                    {canAct && (
                        <>
                            <Button size="sm" leftIcon={<Icon name="check" />} colorScheme="teal" onClick={receive.onOpen}>
                                Registrar Recebimento
                            </Button>
                            <Button size="sm" leftIcon={<Icon name="cancel" />} colorScheme="red" variant="outline" onClick={reject.onOpen}>
                                Rejeitar NF-e
                            </Button>
                        </>
                    )}
                    {canCancel && (
                        <Button size="sm" leftIcon={<Icon name="cancel" />} colorScheme="red" onClick={cancel.onOpen}>
                            Cancelar NF-e
                        </Button>
                    )}
                </ButtonGroup>
            </Heading>

            {/* Cabeçalho de metadados */}
            <MetaHeader data={data} status={status} />

            <Divider my={6} />

            {/* Itens */}
            <Text fontWeight="semibold" fontSize="md" mb={3}>Itens da NF-e</Text>
            <Table columns={[
                { children: 'Produto' },
                { children: 'Qtd. Pedida', isNumeric: true },
                { children: 'Qtd. Recebida', isNumeric: true },
                { children: 'Pendente', isNumeric: true },
                { children: 'Custo un.', isNumeric: true },
                { children: 'Lotes' },
                { children: 'Status' },
            ]}>
                {data?.items?.map(item => {
                    const itemStatus = item.status === 'received'
                        ? { label: 'Recebido',   colorScheme: 'green'  }
                        : item.status === 'divergent'
                        ? { label: 'Divergente', colorScheme: 'orange' }
                        : { label: 'Pendente',   colorScheme: 'yellow' }
                    return (
                        <Row key={item.id}>
                            <Td fontWeight="medium">{item.product?.name}</Td>
                            <Td isNumeric>{item.ordered_qty}</Td>
                            <Td isNumeric>{item.received_qty}</Td>
                            <Td isNumeric fontWeight={item.pending_qty > 0 ? 'bold' : 'normal'}
                                color={item.pending_qty > 0 ? 'orange.500' : 'inherit'}>
                                {item.pending_qty}
                            </Td>
                            <Td isNumeric>
                                {item.unit_cost
                                    ? `R$ ${Number(item.unit_cost).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                    : '—'}
                            </Td>
                            <Td>
                                {item.lots?.length > 0 ? (
                                    <Flex gap={1} flexWrap="wrap">
                                        {item.lots.map(l => (
                                            <Box key={l.id}>
                                                <Badge colorScheme="purple" fontSize="xs">
                                                    {l.lot_number}
                                                </Badge>
                                                {l.expires_at && (
                                                    <Text fontSize="2xs" color="gray.500">
                                                        val. {new Date(l.expires_at).toLocaleDateString('pt-BR')}
                                                    </Text>
                                                )}
                                            </Box>
                                        ))}
                                    </Flex>
                                ) : <Text color="gray.400" fontSize="sm">—</Text>}
                            </Td>
                            <Td><Tag colorScheme={itemStatus.colorScheme}>{itemStatus.label}</Tag></Td>
                        </Row>
                    )
                })}
            </Table>

            <Divider my={6} />

            {/* Evidências */}
            <EvidenceSection invoiceId={id} attachments={data?.attachments ?? []} onRefresh={invoice.refetch} />

            {/* Modais */}
            <ReceiveModal {...receive} invoice={data} onSuccess={invoice.refetch} />
            <RejectModal  {...reject}  invoice={data} onSuccess={() => { invoice.refetch(); navigate('/stock/invoices') }} />
            <CancelModal  {...cancel}  invoice={data} onSuccess={() => { invoice.refetch(); navigate('/stock/invoices') }} />
        </Container>
    )
}

// ─── Bloco de metadados do cabeçalho ─────────────────────────────────────────

const MetaHeader = ({ data, status }) => {
    const cardBg     = useColorModeValue('gray.50',  'gray.700')
    const cardBorder = useColorModeValue('gray.100', 'gray.600')
    const labelColor = useColorModeValue('gray.500', 'gray.400')

    return (
        <Flex
            gap={6} mt={5} flexWrap="wrap"
            bg={cardBg} border="1px solid" borderColor={cardBorder}
            borderRadius="lg" px={5} py={4}
        >
            <Box>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={labelColor} mb={1}>
                    Status
                </Text>
                <Tag colorScheme={status.colorScheme}>{status.label}</Tag>
            </Box>
            <Box>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={labelColor} mb={1}>
                    Núcleo de Destino
                </Text>
                <Text fontSize="md" fontWeight="semibold">{data?.distributor?.name}</Text>
            </Box>
            <Box>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={labelColor} mb={1}>
                    Registrado por
                </Text>
                <Text fontSize="md">{data?.employee?.name}</Text>
            </Box>
            <Box>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={labelColor} mb={1}>
                    Data de Emissão
                </Text>
                <Text fontSize="md">
                    {data?.issued_at ? new Date(data.issued_at).toLocaleDateString('pt-BR') : '—'}
                </Text>
            </Box>
            {data?.key && (
                <Box flex={1} minW="200px">
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={labelColor} mb={1}>
                        Chave NF-e
                    </Text>
                    <Badge
                        colorScheme="blue" variant="subtle"
                        fontFamily="mono" fontSize="xs"
                        wordBreak="break-all" whiteSpace="normal"
                        px={2} py={1} borderRadius="md"
                    >
                        {data.key}
                    </Badge>
                </Box>
            )}
            {data?.notes && (
                <Box>
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={labelColor} mb={1}>
                        Observações
                    </Text>
                    <Text fontSize="md">{data.notes}</Text>
                </Box>
            )}
        </Flex>
    )
}

// ─── Seção de evidências ──────────────────────────────────────────────────────

const EvidenceSection = ({ invoiceId, attachments, onRefresh }) => {
    const toast      = useToast()
    const fileInput  = useRef()
    const [uploading, setUploading] = useState(false)
    const [progress, setProgress]   = useState(0)

    const handleFiles = async (e) => {
        const files = Array.from(e.target.files)
        if (!files.length) return

        setUploading(true)
        setProgress(0)
        let done = 0
        let errors = 0

        for (const file of files) {
            try {
                const fd = new FormData()
                fd.append('file', file)
                await API.post(`/invoices/${invoiceId}/attachments`, fd)
                done++
            } catch (err) {
                errors++
                console.error('Upload error:', err)
            }
            setProgress(Math.round(((done + errors) / files.length) * 100))
        }

        setUploading(false)
        fileInput.current.value = ''

        if (errors === 0) {
            toast({ title: `${done} arquivo(s) enviado(s) com sucesso`, status: 'success' })
        } else if (done > 0) {
            toast({ title: `${done} enviado(s), ${errors} falhou`, status: 'warning' })
        } else {
            toast({ title: 'Falha ao enviar os arquivos. Verifique se o bucket de storage está configurado.', status: 'error' })
        }
        onRefresh?.()
    }

    const typeIcon = (type) => {
        if (type === 'image') return 'photo'
        if (type === 'video') return 'video'
        if (type === 'pdf')   return 'pdf'
        return 'file'
    }

    return (
        <Box>
            <Flex align="center" justify="space-between" mb={4}>
                <Text fontWeight="semibold" fontSize="md">Evidências / Conferência</Text>
                <Box>
                    <input
                        ref={fileInput}
                        type="file"
                        accept="image/*,video/*,.pdf,.xml"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFiles}
                    />
                    <Button
                        size="xs"
                        leftIcon={<Icon name="new" />}
                        colorScheme="teal"
                        variant="outline"
                        isLoading={uploading}
                        loadingText="Enviando..."
                        onClick={() => fileInput.current.click()}
                    >
                        Adicionar Evidência
                    </Button>
                </Box>
            </Flex>

            {uploading && <Progress value={progress} size="xs" colorScheme="teal" mb={4} borderRadius="full" />}

            {attachments.length === 0 && !uploading && (
                <Flex direction="column" align="center" py={8} color="gray.400" gap={2}>
                    <Icon name="invoices" boxSize={8} />
                    <Text fontSize="sm">Nenhuma evidência adicionada</Text>
                    <Text fontSize="xs">Adicione fotos, vídeos ou PDF da conferência de mercadorias</Text>
                </Flex>
            )}

            {attachments.length > 0 && (
                <Flex gap={3} flexWrap="wrap">
                    {attachments.map(att => (
                        <Box key={att.id} p={3} border="1px solid" borderColor="gray.200"
                            _dark={{ borderColor: 'gray.600' }}
                            borderRadius="lg" minW="180px" maxW="220px"
                            display="flex" flexDirection="column" gap={1}>
                            <Flex align="center" gap={2}>
                                <Icon name={typeIcon(att.type)} boxSize={4} color="teal.500" />
                                <Badge colorScheme="gray" fontSize="xs" textTransform="uppercase">
                                    {att.type}
                                </Badge>
                            </Flex>
                            <Text fontSize="sm" noOfLines={2} fontWeight="medium" flex={1}>{att.filename}</Text>
                            <Text fontSize="xs" color="GrayText">{att.employee?.name}</Text>
                            {att.notes && (
                                <Text fontSize="xs" color="gray.500">{att.notes}</Text>
                            )}
                            {att.url ? (
                                <Flex gap={2} mt={1}>
                                    <Button
                                        as="a" href={att.url} target="_blank" rel="noopener noreferrer"
                                        size="xs" colorScheme="teal" variant="outline"
                                        leftIcon={<Icon name="search" boxSize={3} />} flex={1}>
                                        Visualizar
                                    </Button>
                                    <Button
                                        as="a" href={att.url} download={att.filename}
                                        size="xs" colorScheme="gray" variant="outline"
                                        leftIcon={<Icon name="fiscal" boxSize={3} />}>
                                        Baixar
                                    </Button>
                                </Flex>
                            ) : (
                                <Text fontSize="2xs" color="orange.400" mt={1}>
                                    URL indisponível
                                </Text>
                            )}
                        </Box>
                    ))}
                </Flex>
            )}
        </Box>
    )
}

// ─── Modal de recebimento físico ──────────────────────────────────────────────

const ReceiveModal = ({ isOpen, onClose, invoice, onSuccess }) => {
    const toast         = useToast()
    const queryClient   = useQueryClient()
    const authenticate  = useDisclosure()
    const [loading, setLoading]       = useState(false)
    const [quantities, setQuantities] = useState({})

    const cardBg     = useColorModeValue('white',    'gray.700')
    const cardBorder = useColorModeValue('gray.100', 'gray.600')

    const pendingItems = invoice?.items?.filter(i => i.pending_qty > 0) ?? []
    const setQty = (itemId, value) => setQuantities(q => ({ ...q, [itemId]: value }))

    const validateAndOpenAuth = () => {
        const items = Object.entries(quantities).filter(([, v]) => Number(v) > 0)
        if (!items.length) {
            return toast({ title: 'Informe ao menos uma quantidade', status: 'warning' })
        }
        authenticate.onOpen()
    }

    const handleSubmit = async () => {
        const items = Object.entries(quantities)
            .filter(([, v]) => Number(v) > 0)
            .map(([invoice_item_id, received_qty]) => ({ invoice_item_id, received_qty: Number(received_qty) }))

        setLoading(true)
        try {
            await API.post(`/invoices/${invoice?.id}/receive`, { items })
            toast({ title: 'Recebimento registrado com sucesso', status: 'success' })
            queryClient.invalidateQueries({ queryKey: ['invoice', invoice?.id] })
            queryClient.invalidateQueries({ queryKey: ['invoices-list'] })
            onSuccess?.()
            onClose()
            setQuantities({})
        } catch (e) {
            toast({ title: e?.response?.data?.message ?? 'Erro ao registrar recebimento', status: 'error' })
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title="Registrar Recebimento Físico" size="xl">
                <ModalBody pb={2}>
                    <Box
                        bg="blue.50" border="1px solid" borderColor="blue.100"
                        _dark={{ bg: 'blue.900', borderColor: 'blue.700' }}
                        borderRadius="md" px={4} py={3} mb={5}
                    >
                        <Text fontSize="sm" color="blue.700" _dark={{ color: 'blue.200' }}>
                            Informe a quantidade fisicamente conferida para cada item pendente.
                            Itens não preenchidos serão ignorados. A autenticação é obrigatória.
                        </Text>
                    </Box>
                    <Flex direction="column" gap={4}>
                        {pendingItems.map(item => (
                            <Flex key={item.id} align="center" gap={4}
                                p={3} borderRadius="md"
                                border="1px solid" borderColor={cardBorder} bg={cardBg}
                            >
                                <Box flex={1}>
                                    <Text fontSize="sm" fontWeight="semibold">{item.product?.name}</Text>
                                    <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.400' }} mt={0.5}>
                                        Pendente: <Box as="span" fontWeight="bold" color="orange.500">{item.pending_qty} un.</Box>
                                    </Text>
                                    {item.lots?.length > 0 && (
                                        <Flex gap={1} mt={1.5} flexWrap="wrap">
                                            {item.lots.map(l => (
                                                <Badge key={l.id} colorScheme="purple" fontSize="2xs">
                                                    {l.lot_number}{l.expires_at ? ` · val. ${new Date(l.expires_at).toLocaleDateString('pt-BR')}` : ''}
                                                </Badge>
                                            ))}
                                        </Flex>
                                    )}
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.400' }} mb={1} textAlign="center">
                                        Qtd. recebida
                                    </Text>
                                    <Input
                                        size="md" type="number" min={0} max={item.pending_qty} w="100px"
                                        textAlign="center" fontWeight="bold"
                                        placeholder="0"
                                        value={quantities[item.id] ?? ''}
                                        onChange={e => setQty(item.id, e.target.value)}
                                    />
                                </Box>
                            </Flex>
                        ))}
                    </Flex>
                </ModalBody>
                <ModalFooter
                    borderTop="1px solid" borderColor="gray.100" _dark={{ borderColor: 'gray.600' }}
                    mt={4}
                >
                    <ButtonGroup size="md">
                        <Button variant="ghost" onClick={onClose} isDisabled={loading}>Cancelar</Button>
                        <Button colorScheme="teal" isLoading={loading} onClick={validateAndOpenAuth}
                            leftIcon={<Icon name="check" />}>
                            Confirmar Recebimento
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>

            <Authenticate {...authenticate} onSuccess={handleSubmit} />
        </>
    )
}

// ─── Modal de cancelamento com autenticação ───────────────────────────────────

const CancelModal = ({ isOpen, onClose, invoice, onSuccess }) => {
    const toast        = useToast()
    const queryClient  = useQueryClient()
    const authenticate = useDisclosure()
    const [loading, setLoading] = useState(false)
    const [reason, setReason]   = useState('')

    const validateAndOpenAuth = () => {
        if (!reason.trim() || reason.trim().length < 10) {
            return toast({ title: 'Descreva o motivo com pelo menos 10 caracteres', status: 'warning' })
        }
        authenticate.onOpen()
    }

    const handleCancel = async () => {
        setLoading(true)
        try {
            await API.post(`/invoices/${invoice?.id}/cancel`, { reason })
            toast({ title: 'NF-e cancelada. Estoque revertido.', status: 'info', duration: 5000, isClosable: true })
            queryClient.invalidateQueries({ queryKey: ['invoices-list'] })
            queryClient.invalidateQueries({ queryKey: ['invoice', invoice?.id] })
            onSuccess?.()
            handleClose()
        } catch (e) {
            toast({ title: e?.response?.data?.message ?? 'Erro ao cancelar NF-e', status: 'error', isClosable: true })
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => { setReason(''); onClose() }

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose} title="Cancelar NF-e" size="lg">
                <ModalBody pb={2}>
                    <Box
                        bg="red.50" border="1px solid" borderColor="red.200"
                        _dark={{ bg: 'red.900', borderColor: 'red.700' }}
                        borderRadius="md" px={4} py={3} mb={5}
                    >
                        <Text fontSize="sm" color="red.700" _dark={{ color: 'red.200' }} fontWeight="semibold" mb={1}>
                            ⚠️ Ação irreversível — leia antes de continuar
                        </Text>
                        <Text fontSize="sm" color="red.600" _dark={{ color: 'red.300' }}>
                            O cancelamento irá <Box as="span" fontWeight="bold">reverter todo o estoque</Box> que
                            entrou por esta nota. A ação é bloqueada se o estoque já foi consumido por vendas.
                            O motivo ficará registrado no histórico da nota.
                        </Text>
                    </Box>
                    <Textarea
                        placeholder="Ex: Nota fiscal emitida incorretamente, produto devolvido ao fornecedor..."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        rows={4}
                        resize="vertical"
                        _dark={{ borderColor: 'gray.600', bg: 'gray.700' }}
                    />
                    {reason.length > 0 && (
                        <Text fontSize="xs" color="gray.400" mt={1}>{reason.length} caracteres</Text>
                    )}
                </ModalBody>
                <ModalFooter
                    borderTop="1px solid" borderColor="gray.100" _dark={{ borderColor: 'gray.600' }}
                    mt={4}
                >
                    <ButtonGroup size="md">
                        <Button variant="ghost" onClick={handleClose} isDisabled={loading}>Fechar</Button>
                        <Button colorScheme="red" isLoading={loading} onClick={validateAndOpenAuth}
                            leftIcon={<Icon name="cancel" />}>
                            Cancelar NF-e e reverter estoque
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>

            <Authenticate {...authenticate} onSuccess={handleCancel} />
        </>
    )
}

// ─── Modal de rejeição com autenticação ──────────────────────────────────────

const RejectModal = ({ isOpen, onClose, invoice, onSuccess }) => {
    const toast       = useToast()
    const queryClient = useQueryClient()
    const authenticate = useDisclosure()
    const [loading, setLoading] = useState(false)
    const [reason, setReason]   = useState('')

    const validateAndOpenAuth = () => {
        if (!reason.trim()) {
            return toast({ title: 'O motivo da rejeição é obrigatório', status: 'warning' })
        }
        if (reason.trim().length < 10) {
            return toast({ title: 'Descreva o motivo com pelo menos 10 caracteres', status: 'warning' })
        }
        authenticate.onOpen()
    }

    const handleReject = async () => {
        setLoading(true)
        try {
            await API.post(`/invoices/${invoice?.id}/reject`, { reason })
            toast({ title: 'NF-e rejeitada com sucesso', status: 'info' })
            queryClient.invalidateQueries({ queryKey: ['invoices-list'] })
            onSuccess?.()
            handleClose()
        } catch (e) {
            toast({ title: e?.response?.data?.message ?? 'Erro ao rejeitar NF-e', status: 'error' })
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setReason('')
        onClose()
    }

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose} title="Rejeitar NF-e" size="lg">
                <ModalBody pb={2}>
                    <Box
                        bg="red.50" border="1px solid" borderColor="red.100"
                        _dark={{ bg: 'red.900', borderColor: 'red.700' }}
                        borderRadius="md" px={4} py={3} mb={5}
                    >
                        <Text fontSize="sm" color="red.700" _dark={{ color: 'red.200' }}>
                            Informe o motivo da rejeição. O estoque bloqueado dos itens pendentes será{' '}
                            <Box as="span" fontWeight="bold">liberado imediatamente</Box>.{' '}
                            A autenticação é obrigatória para confirmar esta ação.
                        </Text>
                    </Box>
                    <Textarea
                        placeholder="Ex: Nota com divergência fiscal, carga avariada, produto incorreto..."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        rows={4}
                        resize="vertical"
                        _dark={{ borderColor: 'gray.600', bg: 'gray.700' }}
                    />
                    {reason.length > 0 && (
                        <Text fontSize="xs" color="gray.400" _dark={{ color: 'gray.500' }} mt={1}>
                            {reason.length} caracteres
                        </Text>
                    )}
                </ModalBody>
                <ModalFooter
                    borderTop="1px solid" borderColor="gray.100" _dark={{ borderColor: 'gray.600' }}
                    mt={4}
                >
                    <ButtonGroup size="md">
                        <Button variant="ghost" onClick={handleClose} isDisabled={loading}>Cancelar</Button>
                        <Button colorScheme="red" isLoading={loading} onClick={validateAndOpenAuth}
                            leftIcon={<Icon name="cancel" />}>
                            Rejeitar NF-e
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>

            <Authenticate {...authenticate} onSuccess={handleReject} />
        </>
    )
}
